import React, { useCallback, useEffect, useMemo, useState } from "react";
import arcLogo from "../../assets/ARCPlusPrimary.svg";
import raqLogo from "../../assets/RAQPlusPrimary.svg";
import { RouteConstants } from "../../constants/route-constant";
import ReusableTabs from "../Shared/tabs";
import { Box, LoadingOverlay } from "@mantine/core";
import { setMessageCount } from "../../store/messageReducer";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { NoConnectionPage } from "../Shared/NoConnection";
import { FAVICON_CONST } from "../../constants/arcplus-endpoint-constants";
import {
  setCDICount,
  setMyQueriesCount,
  setOpcdHimAlertsCount,
  setOpcdQueriesCount,
  setOpcdCodingAlertsCount,
  setProvider,
  setUmQueriesCount,
  setVBCCount,
} from "../../store/providerReducer";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getModuleIdFromPath,
  isComingSoonModule,
} from "../../constants/module-constants";
import {
  extractCDIAlertsV2,
  normalizeCDIAlertStatus,
} from "../../utils/cdi-alert-utils";
import {
  buildVBCAlertRows,
  countVisibleVBCAlertRows,
  extractVBCAlertsV2,
} from "../../utils/vbc-alert-utils";
import {
  useCDIAlertsV2,
  useMyQueriesOpenCount,
  useOpcdHimAlertsCount,
  useOpcdQueriesPendingCount,
  useOpcdCodingAlertsCount,
  useProviderDetails,
  useUmQueriesOpenCount,
  useUnreadConversationCount,
  useVBCAlerts,
} from "../../hooks/queries";
import CairePatientDetailsChip from "../CAiRE/CairePatientDetailsChip";

const bulwarkLogo = "/bulwark_logo-new.png";

const TabLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const moduleId = getModuleIdFromPath(location.pathname);
  const isCdiModule = moduleId === "cdi";
  const isVbcModule = moduleId === "vbc";
  const isOpcdModule = moduleId === "opcd";
  const isUmModule = moduleId === "um";
  const isCaireModule = moduleId === "caire";
  const careLogo = "/care-logo.png";
  const auricleLogo = "/auricle-logo.webp";
  const isOnVbcPage = location.pathname.startsWith(
    RouteConstants.ROUTE_VBC_ALERTS
  );
  const isOnCdiPage = location.pathname.startsWith(
    RouteConstants.ROUTE_CDI_ALERTS
  );
  const isOnMyQueriesPage = location.pathname.startsWith(
    RouteConstants.ROUTE_MY_QUERIES
  );
  const isOnUmQueriesPage = location.pathname.startsWith(
    RouteConstants.ROUTE_UM_QUERIES
  );
  const isOnUmMyQueriesPage = location.pathname.startsWith(
    RouteConstants.ROUTE_UM_MY_QUERIES
  );
  const isOnUmAlertsPage = location.pathname.startsWith(
    RouteConstants.ROUTE_UM_ALERTS
  );
  const isOnOpcdQueriesPage = location.pathname.startsWith(
    RouteConstants.ROUTE_OPCD_QUERIES
  );
  const isOnOpcdHimAlertsPage = location.pathname.startsWith(
    RouteConstants.ROUTE_OPCD_HIM_ALERTS
  );
  const isOnOpcdCodingAlertsPage = location.pathname.startsWith(
    RouteConstants.ROUTE_OPCD_CODING_ALERTS
  );
  const notificationCount = useAppSelector((state) => state.messages.count);
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  const fhirState = useAppSelector((state) => state.fhir);

  const {
    vbcCount,
    cdiCount,
    myQueriesCount,
    umQueriesCount,
    opcdQueriesCount,
    opcdHimAlertsCount,
    opcdCodingAlertsCount,
  } = useAppSelector((state) => state?.provider);

  const [loading, setLoading] = useState<boolean>(false);
  const [showNoConnection, setShowNoConnection] = useState<boolean>(false);

  const shouldFetchVbc = isVbcModule && !isOnVbcPage;
  // TEMP: UM Alerts reuses CDI alerts counts/endpoints until UM Alerts API exists
  const shouldFetchCdi =
    (isCdiModule && !isOnCdiPage) || (isUmModule && !isOnUmAlertsPage);

  const { data: providerDetailsResponse } = useProviderDetails(providerNPI);
  const { data: messageCountData, refetch: refetchMessageCount } =
    useUnreadConversationCount(providerNPI, {
      // TEMP: UM Messages reuses CDI unread-count endpoint
      polling: isCdiModule || isUmModule,
      enabled: (isCdiModule || isUmModule) && Boolean(providerNPI),
    });
  const { data: openQueriesCountData, refetch: refetchMyQueriesCount } =
    useMyQueriesOpenCount(providerNPI, {
      polling: isCdiModule && !isOnMyQueriesPage,
      enabled:
        isCdiModule && !isOnMyQueriesPage && Boolean(providerNPI),
    });
  const { data: umQueriesCountData, refetch: refetchUmQueriesCount } =
    useUmQueriesOpenCount(providerNPI, {
      polling:
        isUmModule && !isOnUmQueriesPage && !isOnUmMyQueriesPage,
      enabled:
        isUmModule &&
        !isOnUmQueriesPage &&
        !isOnUmMyQueriesPage &&
        Boolean(providerNPI),
    });
  const { data: opcdQueriesCountData, refetch: refetchOpcdQueriesCount } =
    useOpcdQueriesPendingCount(providerNPI, {
      polling: isOpcdModule && !isOnOpcdQueriesPage,
      enabled:
        isOpcdModule && !isOnOpcdQueriesPage && Boolean(providerNPI),
    });
  const { data: opcdHimAlertsCountData, refetch: refetchOpcdHimAlertsCount } =
    useOpcdHimAlertsCount(providerNPI, {
      polling: isOpcdModule && !isOnOpcdHimAlertsPage,
      enabled:
        isOpcdModule && !isOnOpcdHimAlertsPage && Boolean(providerNPI),
    });
  const {
    data: opcdCodingAlertsCountData,
    refetch: refetchOpcdCodingAlertsCount,
  } = useOpcdCodingAlertsCount({
    polling: isOpcdModule && !isOnOpcdCodingAlertsPage,
    enabled: isOpcdModule && !isOnOpcdCodingAlertsPage,
  });
  const { data: vbcResponse, refetch: refetchVbcAlerts } = useVBCAlerts(
    fhirState?.mrn ?? undefined,
    {
      polling: shouldFetchVbc,
      enabled: shouldFetchVbc && Boolean(fhirState?.mrn),
    }
  );
  const { data: cdiResponse, refetch: refetchCdiAlerts } = useCDIAlertsV2(
    { mrn_no: fhirState?.mrn ?? "", status_filter: null },
    {
      polling: shouldFetchCdi,
      enabled: shouldFetchCdi && Boolean(fhirState?.mrn),
    }
  );

  useEffect(() => {
    if (providerDetailsResponse?.data?.data) {
      dispatch(setProvider(providerDetailsResponse.data.data));
    }
  }, [dispatch, providerDetailsResponse]);

  useEffect(() => {
    if (messageCountData !== undefined) {
      dispatch(setMessageCount(messageCountData));
    }
  }, [dispatch, messageCountData]);

  useEffect(() => {
    if (openQueriesCountData !== undefined) {
      dispatch(setMyQueriesCount(openQueriesCountData));
    }
  }, [dispatch, openQueriesCountData]);

  useEffect(() => {
    if (umQueriesCountData !== undefined) {
      dispatch(setUmQueriesCount(umQueriesCountData));
    }
  }, [dispatch, umQueriesCountData]);

  useEffect(() => {
    if (opcdQueriesCountData !== undefined) {
      dispatch(setOpcdQueriesCount(opcdQueriesCountData));
    }
  }, [dispatch, opcdQueriesCountData]);

  useEffect(() => {
    if (opcdHimAlertsCountData !== undefined) {
      dispatch(setOpcdHimAlertsCount(opcdHimAlertsCountData));
    }
  }, [dispatch, opcdHimAlertsCountData]);

  useEffect(() => {
    if (opcdCodingAlertsCountData !== undefined) {
      dispatch(setOpcdCodingAlertsCount(opcdCodingAlertsCountData));
    }
  }, [dispatch, opcdCodingAlertsCountData]);

  useEffect(() => {
    if (!shouldFetchVbc) return;

    if (vbcResponse?.status === 200) {
      const vbcAlerts = buildVBCAlertRows(extractVBCAlertsV2(vbcResponse.data));
      dispatch(setVBCCount(countVisibleVBCAlertRows(vbcAlerts)));
    }
  }, [dispatch, shouldFetchVbc, vbcResponse]);

  useEffect(() => {
    if (!shouldFetchCdi) return;

    if (cdiResponse?.status === 200) {
      const cdiAlerts = extractCDIAlertsV2(cdiResponse.data);
      dispatch(
        setCDICount(
          cdiAlerts.filter(
            (alert) => normalizeCDIAlertStatus(alert.status) === "PENDING"
          ).length
        )
      );
    }
  }, [cdiResponse, dispatch, shouldFetchCdi]);

  // Check internet connection
  const checkConnection = useCallback(async () => {
    try {
      // Check if navigator.onLine is false first
      if (!navigator.onLine) {
        setShowNoConnection(true);
        return false;
      }

      // Additional check by trying to fetch a small resource
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      await fetch(FAVICON_CONST, {
        method: "HEAD",
        mode: "no-cors",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      setShowNoConnection(false);
      return true;
    } catch (error) {
      console.log("Connection check failed:", error);
      setShowNoConnection(true);
      return false;
    }
  }, []);

  const handleRetryConnection = useCallback(async () => {
    setLoading(true);
    const isConnected = await checkConnection();
    if (isConnected) {
      if (isCdiModule) {
        await Promise.all([refetchMessageCount(), refetchMyQueriesCount()]);
      }
      if (isUmModule) {
        await Promise.all([refetchUmQueriesCount(), refetchMessageCount()]);
      }
      if (isOpcdModule) {
        await Promise.all([
          refetchOpcdQueriesCount(),
          refetchOpcdHimAlertsCount(),
          refetchOpcdCodingAlertsCount(),
        ]);
      }
      await Promise.all([refetchVbcAlerts(), refetchCdiAlerts()]);
    } else {
      setLoading(false);
    }
  }, [
    checkConnection,
    isCdiModule,
    isOpcdModule,
    isUmModule,
    refetchCdiAlerts,
    refetchMessageCount,
    refetchMyQueriesCount,
    refetchOpcdCodingAlertsCount,
    refetchOpcdHimAlertsCount,
    refetchOpcdQueriesCount,
    refetchUmQueriesCount,
    refetchVbcAlerts,
  ]);

  useEffect(() => {
    const handleOnline = () => {
      setShowNoConnection(false);
      if (isCdiModule) {
        void refetchMessageCount();
        void refetchMyQueriesCount();
      }
      if (isUmModule) {
        void refetchUmQueriesCount();
        void refetchMessageCount();
        void refetchCdiAlerts();
      }
      if (isOpcdModule) {
        void refetchOpcdQueriesCount();
        void refetchOpcdHimAlertsCount();
        void refetchOpcdCodingAlertsCount();
      }
      if (fhirState?.mrn && (isCdiModule || isVbcModule || isUmModule)) {
        void refetchVbcAlerts();
        void refetchCdiAlerts();
      }
    };

    const handleOffline = () => {
      setShowNoConnection(true);
      setLoading(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    void checkConnection();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [
    checkConnection,
    fhirState?.mrn,
    isCdiModule,
    isOpcdModule,
    isUmModule,
    isVbcModule,
    refetchCdiAlerts,
    refetchMessageCount,
    refetchMyQueriesCount,
    refetchOpcdCodingAlertsCount,
    refetchOpcdHimAlertsCount,
    refetchOpcdQueriesCount,
    refetchUmQueriesCount,
    refetchVbcAlerts,
  ]);

  const cdiTabs = useMemo(
    () => [
      {
        value: "Patient-CDI-Query",
        label: "CDI Query",
        route: RouteConstants.ROUTE_CDI_MODULE,
      },
      {
        value: "Provider-Dashboard",
        label: "Reports",
        route: RouteConstants.ROUTE_DASHBOARD,
      },
      {
        value: "My-Queries",
        label: "My Queries",
        route: RouteConstants.ROUTE_MY_QUERIES,
        notificationCount: myQueriesCount,
      },
      {
        value: "messages",
        label: "Messages",
        route: RouteConstants.ROUTE_MESSAGES,
        notificationCount: Number(notificationCount) || 0,
      },
      {
        value: "alerts1",
        label: (
          <Box
            component="span"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            CDI Alerts
            <Box
              component="img"
              src="/ai-bulwark-logo.svg"
              alt=""
              aria-hidden
              h={24}
              w="auto"
              style={{
                flexShrink: 0,
                objectFit: "contain",
                paddingTop: 2,
                // filter: "contrast(1.45) saturate(1.35)",
              }}
            />
          </Box>
        ),
        route: RouteConstants.ROUTE_CDI_ALERTS,
        isAlert: true,
        notificationCount: cdiCount,
      },
      {
        value: "encounter-drg",
        label: "Coding Summary",
        route: RouteConstants.ROUTE_ENCOUNTER_DRG,
      },
    ],
    [notificationCount, cdiCount, myQueriesCount]
  );

  const vbcTabs = useMemo(
    () => [
      {
        value: "vbc",
        label: "VBC Alerts",
        route: RouteConstants.ROUTE_VBC_ALERTS,
        isAlert: true,
        notificationCount: vbcCount,
      },
    ],
    [vbcCount]
  );

  const umTabs = useMemo(
    () => [
      {
        value: "um-queries",
        label: "UM Queries",
        route: RouteConstants.ROUTE_UM_QUERIES,
      },
      {
        value: "um-reports",
        label: "Reports",
        route: RouteConstants.ROUTE_UM_REPORTS,
      },
      {
        value: "um-my-queries",
        label: "My Queries",
        route: RouteConstants.ROUTE_UM_MY_QUERIES,
        notificationCount: umQueriesCount,
      },
      {
        value: "um-messages",
        label: "Messages",
        route: RouteConstants.ROUTE_UM_MESSAGES,
        notificationCount: Number(notificationCount) || 0,
      },
      {
        value: "um-alerts",
        label: (
          <Box
            component="span"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            UM Alerts
            <Box
              component="img"
              src="/ai-bulwark-logo.svg"
              alt=""
              aria-hidden
              h={24}
              w="auto"
              style={{
                flexShrink: 0,
                objectFit: "contain",
                paddingTop: 2,
              }}
            />
          </Box>
        ),
        route: RouteConstants.ROUTE_UM_ALERTS,
        isAlert: true,
        // TEMP: badge uses CDI alerts pending count until UM Alerts API exists
        notificationCount: cdiCount,
      },
    ],
    [umQueriesCount, notificationCount, cdiCount]
  );

  const opcdTabs = useMemo(
    () => [
      {
        value: "opcd-queries",
        label: "CDI Queries",
        route: RouteConstants.ROUTE_OPCD_QUERIES,
        notificationCount: opcdQueriesCount,
      },
      {
        value: "opcd-him-alerts",
        label: "HIM Alerts",
        route: RouteConstants.ROUTE_OPCD_HIM_ALERTS,
        isAlert: true,
        notificationCount: opcdHimAlertsCount,
      },
      {
        value: "opcd-coding-alerts",
        label: "Coding Alerts",
        route: RouteConstants.ROUTE_OPCD_CODING_ALERTS,
        isAlert: true,
        notificationCount: opcdCodingAlertsCount,
      },
      {
        value: "opcd-coding-results",
        label: "Coding Results",
        route: RouteConstants.ROUTE_OPCD_CODING_RESULTS,
      },
    ],
    [opcdCodingAlertsCount, opcdHimAlertsCount, opcdQueriesCount]
  );

  const caireTabs = useMemo(
    () => [
      {
        value: "caire-predicted-dc-disposition",
        label: "Predicted DC Disposition",
        route: RouteConstants.ROUTE_CAIRE_PREDICTED_DC_DISPOSITION,
      },
      {
        value: "caire-readmission-risk",
        label: "Readmission Risk",
        route: RouteConstants.ROUTE_CAIRE_READMISSION_RISK,
      },
      // DC Readiness tab hidden until the feature is ready
      {
        value: "caire-dc-barriers",
        label: "DC Barriers",
        route: RouteConstants.ROUTE_CAIRE_DC_BARRIERS,
      },
    ],
    []
  );

  const tabs = isVbcModule
    ? vbcTabs
    : isOpcdModule
    ? opcdTabs
    : isUmModule
    ? umTabs
    : isCaireModule
    ? caireTabs
    : cdiTabs;
  const defaultTabRoute = isVbcModule
    ? RouteConstants.ROUTE_VBC_ALERTS
    : isOpcdModule
    ? RouteConstants.ROUTE_OPCD_QUERIES
    : isUmModule
    ? RouteConstants.ROUTE_UM_QUERIES
    : isCaireModule
    ? RouteConstants.ROUTE_CAIRE_PREDICTED_DC_DISPOSITION
    : RouteConstants.ROUTE_CDI_MODULE;
  const hideTabs = isComingSoonModule(moduleId);

  if (fhirState?.hasFailed) {
    navigate(RouteConstants.ROUTE_UNAUTHORIZED);
  }

  return (
    <Box
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: "4px",
      }}
    >
      <LoadingOverlay
        visible={loading}
        zIndex={999}
        overlayProps={{ radius: "sm", blur: "2" }}
      />

      {showNoConnection ? (
        <NoConnectionPage onRetry={handleRetryConnection} />
      ) : (
        <ReusableTabs
          tabs={tabs}
          defaultValue={defaultTabRoute}
          logo={bulwarkLogo}
          logo2={
            isVbcModule
              ? raqLogo
              : isCaireModule
                ? careLogo
                : isUmModule
                  ? auricleLogo
                  : arcLogo
          }
          logo2Wide={isUmModule}
          // logo2Placement={isVbcModule ? "right" : "left"}
          // logo2Tagline={isVbcModule ? "Risk Adjustment & Quality" : undefined}
          hideTabs={hideTabs}
          headerAccessory={
            isCaireModule ? <CairePatientDetailsChip /> : undefined
          }
        />
      )}
    </Box>
  );
};

export default TabLayout;
